/**
 * LocationSearchBar Component
 * Simplified autocomplete search bar that works reliably with Google Maps
 * Based on advisor's working implementation
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

type Props = {
  map: any | null; // google.maps.Map | null
  country?: string;
  onPlaceSelected?: (data: {
    place_id: string;
    name?: string | null;
    formatted_address?: string | null;
    lat: number;
    lng: number;
    phones?: string[];
    website?: string | null;
  }) => void;
  sessionToken?: any; // google.maps.places.AutocompleteSessionToken | null
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>; // External ref for dialog close prevention
  searchQuery?: string; // Controlled input value
  onSearchQueryChange?: (query: string) => void; // Callback when input value changes
  onFocus?: () => void; // Callback when input is focused
  onBlur?: () => void; // Callback when input is blurred
};

export default function LocationSearchBar({ 
  map, 
  country = 'lv', 
  onPlaceSelected,
  sessionToken: externalSessionToken,
  disabled = false,
  inputRef: externalInputRef,
  searchQuery,
  onSearchQueryChange,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
}: Props) {
  const internalInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = externalInputRef || internalInputRef;
  const autocompleteRef = useRef<any>(null); // google.maps.places.Autocomplete | null
  const placesSvcRef = useRef<any>(null); // google.maps.places.PlacesService | null
  const [placesReady, setPlacesReady] = useState(false); // State to trigger re-initialization when Places API loads

    // Create session token if not provided
  const sessionToken = useMemo(() => {
    if (externalSessionToken) return externalSessionToken;
    const googleMaps = window.google?.maps;
    if (googleMaps?.places) {
      return new googleMaps.places.AutocompleteSessionToken();
    }
    return null;
  }, [externalSessionToken]);

  // Initialize autocomplete (only once per input element)
  useEffect(() => {
    // Ref guard: only initialize if autocomplete doesn't exist and input is available
    if (autocompleteRef.current || !inputRef.current) {
      return;
    }

    // Wait for Google Maps Places API to be ready
    if (!window.google?.maps?.places) {
      console.log('[LocationSearchBar] Waiting for Google Maps Places API...');
      // Poll for Places API to become available
      const retryTimer = setTimeout(() => {
        if (window.google?.maps?.places) {
          console.log('[LocationSearchBar] Google Maps Places API is now available, triggering re-initialization...');
          setPlacesReady(prev => !prev); // Toggle to trigger re-run
        } else {
          // Keep polling
          setPlacesReady(prev => !prev); // Toggle to trigger re-run
        }
      }, 500);
      return () => clearTimeout(retryTimer);
    }

    console.log('[LocationSearchBar] Initializing autocomplete...', {
      hasMap: !!map,
      country,
      hasSessionToken: !!sessionToken,
    });

    // Guard: Ensure Google Maps Places is available
    const googleMaps = window.google?.maps;
    if (!googleMaps?.places) {
      console.warn('[LocationSearchBar] Google Maps Places not available, cannot create autocomplete');
      return;
    }

    const ac = new googleMaps.places.Autocomplete(inputRef.current, {
      fields: ['place_id', 'name', 'formatted_address', 'geometry', 'formatted_phone_number', 'international_phone_number', 'website'],
      componentRestrictions: { country: [country] },
      sessionToken: sessionToken || undefined,
    });

    autocompleteRef.current = ac;
    console.log('[LocationSearchBar] ✅ Autocomplete created');

    // Fix dropdown positioning - move pac-container to body and prevent map clicks
    const fixDropdownPosition = () => {
      const pacContainer = document.querySelector('.pac-container') as HTMLElement;
      if (pacContainer && inputRef.current) {
        // Always move to body to ensure it's above everything and clickable
        if (pacContainer.parentElement !== document.body) {
          console.log('[LocationSearchBar] Moving pac-container to body for proper positioning');
          document.body.appendChild(pacContainer);
        }
        
        // Prevent clicks on dropdown from propagating to map
        pacContainer.style.pointerEvents = 'auto';
        pacContainer.style.zIndex = '10000';
        
        // Stop event propagation on all items (only add listeners once)
        const pacItems = pacContainer.querySelectorAll('.pac-item');
        pacItems.forEach((item) => {
          // Check if listener already added
          if (!(item as any).__autocompleteClickHandler) {
            const handler = (e: Event) => {
              e.stopPropagation();
              e.preventDefault();
              console.log('[LocationSearchBar] Autocomplete item clicked, stopping propagation');
            };
            item.addEventListener('click', handler, true);
            (item as any).__autocompleteClickHandler = handler; // Mark as added
          }
        });
      }
    };

    // Check periodically for pac-container and fix position
    const positionCheckInterval = setInterval(fixDropdownPosition, 100);

    // Update bounds when map bounds change
    let boundsListener: google.maps.MapsEventListener | null = null;
    if (map) {
      boundsListener = map.addListener('bounds_changed', () => {
        const bounds = map.getBounds();
        if (bounds) {
          ac.setBounds(bounds);
          console.log('[LocationSearchBar] Bounds updated from map');
        }
      });
    }

    return () => {
      clearInterval(positionCheckInterval);
      if (boundsListener) {
        boundsListener.remove();
      }
      // Clean up autocomplete on unmount (when component is actually removed)
      if (autocompleteRef.current) {
        console.log('[LocationSearchBar] Cleaning up autocomplete on unmount');
        const googleMaps = window.google?.maps;
        if (googleMaps?.event) {
          googleMaps.event.clearInstanceListeners(autocompleteRef.current);
        }
        autocompleteRef.current = null;
      }
    };
  }, [placesReady]); // Re-run when placesReady changes (triggers when Google Maps Places API loads)

  // Update autocomplete options when country or sessionToken change (without recreating)
  useEffect(() => {
    const ac = autocompleteRef.current;
    if (!ac) return;

    // Update options using setOptions instead of recreating
    ac.setOptions({
      componentRestrictions: { country: [country] },
      sessionToken: sessionToken || undefined,
    });
    console.log('[LocationSearchBar] Autocomplete options updated:', { country, hasSessionToken: !!sessionToken });
  }, [country, sessionToken]);

  // Initialize PlacesService
  useEffect(() => {
    const googleMaps = window.google?.maps;
    if (map && !placesSvcRef.current && googleMaps?.places) {
      placesSvcRef.current = new googleMaps.places.PlacesService(map);
      console.log('[LocationSearchBar] PlacesService created');
    }
  }, [map]);

  // Handle place selection
  useEffect(() => {
    const ac = autocompleteRef.current;
    if (!ac || !onPlaceSelected) return;

    const handlePlaceChanged = () => {
      console.log('[LocationSearchBar] ========== place_changed event fired ==========');
      const place = ac.getPlace();
      console.log('[LocationSearchBar] Place object:', {
        place_id: place.place_id,
        name: place.name,
        hasGeometry: !!place.geometry?.location,
      });

      const keep = inputRef.current?.value ?? '';

      const hasGeometry = !!place.geometry?.location;
      const placeId = place.place_id;

      if (!placeId) {
        console.warn('[LocationSearchBar] ❌ No place_id in result');
        return;
      }

      // Normalize phones into array
      const phones: string[] = [];
      if (place.formatted_phone_number) {
        phones.push(...place.formatted_phone_number.split(',').map(p => p.trim()).filter(Boolean));
      }
      if (place.international_phone_number) {
        phones.push(...place.international_phone_number.split(',').map(p => p.trim()).filter(Boolean));
      }
      // Dedupe
      const uniquePhones = Array.from(new Set(phones));

      const emit = (loc: any) => {
        console.log('[LocationSearchBar] Emitting place selection:', {
          place_id: placeId,
          name: place.name,
          lat: loc.lat(),
          lng: loc.lng(),
        });

        // Update map view (only if map is available)
        // Simple approach: just panTo() and let it naturally preserve zoom
        if (map) {
          console.log('[LocationSearchBar] Updating map view to selected place');
          // Use panTo which preserves zoom - no restoration logic needed
          map.panTo(loc);
        } else {
          console.warn('[LocationSearchBar] ⚠️ Map not available, cannot update view');
        }

        // Call parent handler
        onPlaceSelected({
          place_id: placeId,
          name: place.name ?? null,
          formatted_address: place.formatted_address ?? null,
          lat: loc.lat(),
          lng: loc.lng(),
          phones: uniquePhones,
          website: place.website ?? null,
        });

        // Keep the input value
        if (inputRef.current) {
          inputRef.current.value = keep;
          console.log('[LocationSearchBar] Input value kept:', keep);
        }
      };

      if (hasGeometry) {
        console.log('[LocationSearchBar] Has geometry, emitting immediately');
        emit(place.geometry.location);
      } else if (placesSvcRef.current) {
        console.log('[LocationSearchBar] No geometry, fetching details...');
        placesSvcRef.current.getDetails(
          {
            placeId: placeId,
            fields: ['geometry', 'name', 'formatted_address', 'formatted_phone_number', 'international_phone_number', 'website', 'place_id'],
            sessionToken: sessionToken || undefined,
          },
          (details: any, status: any) => {
            console.log('[LocationSearchBar] getDetails callback:', { status, hasDetails: !!details, hasGeometry: !!details?.geometry?.location });
            const googleMaps = window.google?.maps;
            const okStatus = googleMaps?.places?.PlacesServiceStatus?.OK;
            if (!okStatus || status !== okStatus || !details?.geometry?.location) {
              console.warn('[LocationSearchBar] ❌ getDetails failed:', status);
              return;
            }
            emit(details.geometry.location);
          }
        );
      }
    };

    const listener = ac.addListener('place_changed', handlePlaceChanged);
    console.log('[LocationSearchBar] ✅ place_changed listener attached');

    return () => {
      console.log('[LocationSearchBar] Cleaning up place_changed listener');
      listener.remove();
    };
  }, [onPlaceSelected, sessionToken, map]);

  // Handle focus - set bounds from geolocation if map has no bounds
  const handleFocus = () => {
    onFocusProp?.(); // Call parent focus handler
    const googleMaps = window.google?.maps;
    if (!map?.getBounds() && navigator.geolocation && googleMaps) {
      console.log('[LocationSearchBar] Map has no bounds, getting user location...');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          // Guard: Check if Google Maps is available before using it
          if (!googleMaps) {
            console.warn('[LocationSearchBar] Google Maps not available, skipping bounds update');
            return;
          }
          
          const center = new googleMaps.LatLng(pos.coords.latitude, pos.coords.longitude);
          const bounds = new googleMaps.Circle({ center, radius: 1000 }).getBounds();
          if (bounds && autocompleteRef.current) {
            autocompleteRef.current.setBounds(bounds);
            console.log('[LocationSearchBar] Bounds set from geolocation');
          }
          if (map) {
            map.setCenter(center);
            map.setZoom(14);
          }
        },
        (error) => {
          console.warn('[LocationSearchBar] Geolocation failed:', error);
        }
      );
    }
  };

  // Handle blur
  const handleBlur = () => {
    onBlurProp?.(); // Call parent blur handler
  };

  // Use internal state for uncontrolled input when searchQuery is not provided
  const [internalValue, setInternalValue] = useState('');
  const isControlled = searchQuery !== undefined;
  const inputValue = isControlled ? searchQuery : internalValue;

  // Sync internal value with input ref when uncontrolled
  useEffect(() => {
    if (!isControlled && inputRef.current && inputRef.current.value !== internalValue) {
      setInternalValue(inputRef.current.value);
    }
  }, []);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (isControlled) {
      onSearchQueryChange?.(value);
    } else {
      setInternalValue(value);
    }
  };

  // Handle clear button click
  const handleClear = () => {
    if (onSearchQueryChange) {
      onSearchQueryChange('');
    } else {
      setInternalValue('');
    }
    // Also clear the input ref directly
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    // Focus the input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Show clear button only when there's text
  const showClearButton = inputValue.trim().length > 0;

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        placeholder="Search by repair shop name or address"
        className="w-full h-12 rounded-full border-0 pl-4 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#090909',
          fontSize: '14px',
          paddingRight: showClearButton ? '44px' : '16px', // Make room for clear button
        }}
        value={inputValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        autoFocus={false}
        aria-label="Search by repair shop name or address"
      />
      {/* Clear button */}
      {showClearButton && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors min-w-[32px] min-h-[32px]"
          aria-label="Clear search"
          tabIndex={0}
        >
          <X size={16} className="text-gray-500" />
        </button>
      )}
      <style>{`
        input::placeholder {
          color: #868686 !important;
        }
        input:focus {
          outline: 2px solid #bfd0fd !important;
          outline-offset: 2px !important;
        }
      `}</style>
    </div>
  );
}

